import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { usePagination } from "../hooks/usePagination";

interface PaginationProps {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
}) => {
  const { paginationRange, isFirstPage, isLastPage } = usePagination({
    totalCount,
    pageSize,
    currentPage,
  });

  if (totalCount <= pageSize) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 sm:px-6">
      <div className="flex items-center">
        <p className="text-sm text-gray-700">
          Showing{" "}
          <span className="font-medium">
            {(currentPage - 1) * pageSize + 1}
          </span>{" "}
          to{" "}
          <span className="font-medium">
            {Math.min(currentPage * pageSize, totalCount)}
          </span>{" "}
          of <span className="font-medium">{totalCount}</span> results
        </p>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={isFirstPage}
          className={`relative inline-flex items-center px-2 py-2 rounded-md ${
            isFirstPage
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {paginationRange.map((pageNumber, index) => (
          <button
            key={index}
            onClick={() => {
              if (typeof pageNumber === "number") {
                onPageChange(pageNumber);
              }
            }}
            disabled={pageNumber === "..."}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
              pageNumber === currentPage
                ? "z-10 bg-green-600 text-white"
                : pageNumber === "..."
                ? "text-gray-700"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            {pageNumber}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLastPage}
          className={`relative inline-flex items-center px-2 py-2 rounded-md ${
            isLastPage
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
